import { createBrowserHistory } from 'history';

const history = createBrowserHistory();

enum RouteName {
  Home = 'home',
  Account = 'account',
  Collaborators = 'collaborators',
  Templates = 'templates',
  Vaults = 'vaults',
  VaultNew = 'vaultNew',
  VaultDetail = 'vaultDetail',
  VaultImport = 'vaultImport',
  MoveMemories = 'moveMemories',
  DropboxCallback = 'dropboxCallback',
  VaultSettings = 'vaultSettings',
  VaultDetailGuest = 'vaultDetailGuest',
  JoinVideoCallGuest = 'joinVideoCallGuest',
  ClipDetailGuest = 'clipDetailGuest',
  SignIn = 'signIn',
  SignUp = 'signUp',
  EmailWaitingConfirm = 'emailWaitingConfirm',
  EmailVerifying = 'emailVerifying',
  ForgotPassword = 'forgotPassword',
  ResetPassword = 'resetPassword',
  AgoraVideoCall = 'agoraVideoCall',
  NotFound = 'notFound',
  NotFoundRedirect = 'notFoundRedirect',
  Store = 'store',
  UnlinkedQrCode = 'unlinkQrCode',
  SelfRecordingGuest = 'selfRecordingGuest',
  AlbumShareGuest = 'albumShareGuest',
  ReviewVideos = 'reviewVideos',
  ReviewVideosGuest = 'reviewVideosGuest',
  CreateTemporaryUser = 'createTemporaryUser',
  SignUpIgn = 'SignUpIgn',
  VaultReminder = 'VaultReminder',
  StartOnboarding = 'startOnboarding',
  SignUpOnboarding = 'singUpOnboarding',
  SignInOnboarding = 'singInOnboarding',
  OnboardingSubjectSelection = 'onboardingSubjectSelection',
  OnboardingVaultInfo = 'onboardingVaultInfo',
  OnboardingSelectCategories = 'onboardingSelectCategories',
  OnboardingPromptSelectCategories = 'onboardingPromptSelectCategories',
  ViewMode = 'viewMode',
  MemoriesViewScreen = 'memoriesViewScreen',
  AlbumViewMode = 'albumViewMode',
  ContentViewMode = 'contentViewMode',
  VaultViewMode = 'vaultViewMode',
  AlbumScreen = 'albumScreen',
  AlbumViewScreen = 'albumViewScreen',
  AlbumInvite = 'albumInvite',
  Invitation = 'invitation',
}

type Route = {
  name: RouteName;
  path: string;
  exact?: boolean;
  pathParams?: string[];
  component?: React.ComponentType;
};

const PATH_PARAM_REGEX = /:[^/]+/gi;
/**
  Analyzes the path defined for `route` and
  returns a copy of the route with a new attribute
  `pathParams` which is a list of strings that correspond to the path params.

  @param {object} route - Object that represents a route.

  @return {object} updated route with the new attribute.

  @example

    determineRouteParams({ name: 'product', path: '/product/:id', component: ProductPage })
*/
const determineRouteParams = (route: Route): Route => {
  const newRoute = { ...route };
  const { path } = newRoute;
  // Extract the names of the parameters
  const pathMatch = path.match(PATH_PARAM_REGEX) || [];
  const pathParams = pathMatch.map((param) => param.slice(1));
  newRoute.pathParams = pathParams || [];
  return newRoute;
};

const routes = [
  {
    name: RouteName.Home,
    path: '/',
    exact: true,
  },
  {
    name: RouteName.VaultNew,
    path: '/vaults/new',
    exact: true,
  },
  {
    name: RouteName.VaultDetail,
    path: '/vaults/:id',
    exact: true,
  },
  {
    name: RouteName.VaultSettings,
    path: '/vaults/:id/settings',
    exact: true,
  },
  {
    name: RouteName.VaultImport,
    path: '/vaults/:id/import',
    exact: true,
  },
  {
    name: RouteName.MoveMemories,
    path: '/vaults/:id/move-memories',
    exact: true,
  },
  {
    name: RouteName.DropboxCallback,
    path: '/dropbox-callback',
    exact: true,
  },
  {
    name: RouteName.VaultDetailGuest,
    path: '/vaults/share/:id',
    exact: true,
  },
  {
    name: RouteName.JoinVideoCallGuest,
    path: '/vaults/:vaultId/join/:videoCallToken',
  },
  {
    name: RouteName.Vaults,
    path: '/vaults',
    exact: true,
  },
  {
    name: RouteName.Collaborators,
    path: '/collaborators',
    exact: true,
  },
  {
    name: RouteName.ClipDetailGuest,
    path: '/clips/:vaultId/:clipId',
    exact: true,
  },
  {
    name: RouteName.SignIn,
    path: '/signIn',
    exact: true,
  },
  {
    name: RouteName.SignUp,
    path: '/signUp',
    exact: true,
  },
  {
    name: RouteName.EmailWaitingConfirm,
    path: '/waitEmailConfirm',
    exact: true,
  },
  {
    name: RouteName.EmailVerifying,
    path: '/verifyEmail/:emailVerificationToken',
    exact: true,
  },
  {
    name: RouteName.ForgotPassword,
    path: '/forgotPassword',
    exact: true,
  },
  {
    name: RouteName.ResetPassword,
    path: '/resetPassword/:token',
    exact: true,
  },
  {
    name: RouteName.Account,
    path: '/account',
    exact: true,
  },
  {
    name: RouteName.Templates,
    path: '/templates',
    exact: true,
  },
  {
    name: RouteName.AgoraVideoCall,
    path: '/agoraVideoCall/:agoraVaultId/:videoCallToken/:role',
    exact: true,
  },
  {
    name: RouteName.Store,
    path: '/store',
    exact: true,
  },
  {
    name: RouteName.SelfRecordingGuest,
    path: '/guest/:guestToken/vaults/:vaultId',
    exact: true,
  },
  {
    name: RouteName.AlbumShareGuest,
    path: '/guest/:guestToken/vaults/:vaultId/album/:albumId',
    exact: true,
  },
  {
    name: RouteName.ReviewVideosGuest,
    path: '/guest/:guestToken/reviewVideos',
    exact: true,
  },
  {
    name: RouteName.UnlinkedQrCode,
    path: '/unlinked-qr-code',
    exact: true,
  },
  {
    name: RouteName.NotFoundRedirect,
    path: '/not-found',
    exact: true,
  },
  {
    name: RouteName.ReviewVideos,
    path: '/reviewVideos',
    exact: true,
  },
  {
    name: RouteName.CreateTemporaryUser,
    path: '/createTemporaryUser',
    exact: true,
  },
  {
    name: RouteName.SignUpIgn,
    path: '/signUpInheritGuard',
    exact: true,
  },
  {
    name: RouteName.VaultReminder,
    path: '/vault-reminder',
    exact: true,
  },
  {
    name: RouteName.StartOnboarding,
    path: '/startOnboarding',
    exact: true,
  },
  {
    name: RouteName.SignUpOnboarding,
    path: '/signUpOnboarding',
    exact: true,
  },
  {
    name: RouteName.SignInOnboarding,
    path: '/signInOnboarding',
    exact: true,
  },
  {
    name: RouteName.OnboardingSubjectSelection,
    path: '/onboardingSubjectSelection',
    exact: true,
  },
  {
    name: RouteName.OnboardingVaultInfo,
    path: '/onboardingVaultInfo',
    exact: true,
  },
  {
    name: RouteName.OnboardingSelectCategories,
    path: '/selectCategories',
    exact: true,
  },
  {
    name: RouteName.OnboardingPromptSelectCategories,
    path: '/promptSelectCategories',
    exact: true,
  },
  {
    // The old vault view mode; forwards to the new screens (see
    // pages/album-view-mode/legacy-vault-view-mode).
    name: RouteName.ViewMode,
    path: '/vaults/:id/view-mode',
    exact: true,
  },
  {
    name: RouteName.MemoriesViewScreen,
    path: '/vaults/:id/memories/view',
    exact: true,
  },
  {
    name: RouteName.AlbumViewMode,
    path: '/view-mode/album/:albumId',
    exact: true,
  },
  {
    name: RouteName.AlbumScreen,
    path: '/vaults/:id/album/:albumId',
    exact: true,
  },
  {
    name: RouteName.AlbumViewScreen,
    path: '/vaults/:id/album/:albumId/view',
    exact: true,
  },
  {
    name: RouteName.AlbumInvite,
    path: '/albums/invite/:albumId',
    exact: true,
  },
  {
    // Any invitation (vault, album or memory) from its email.
    name: RouteName.Invitation,
    path: '/invite/:token',
    exact: true,
  },
  {
    name: RouteName.ContentViewMode,
    path: '/view-mode/content/:contentId',
    exact: true,
  },
  {
    // A vault's public memories (vault QR codes, signed-out vault visitors).
    name: RouteName.VaultViewMode,
    path: '/view-mode/vault/:vaultId',
    exact: true,
  },
  {
    name: RouteName.NotFound,
    path: '/*',
  },
].map(determineRouteParams);

export { determineRouteParams, history, RouteName, routes };
